import React, { useState, forwardRef, useCallback, useEffect } from 'react'
import {
  Stack,
  Box,
  Radio,
  Inline,
  Checkbox,
  Spinner,
  Card,
  Flex,
  Text,
  Label,
} from '@sanity/ui'
import { set, unset } from 'sanity'
import { useClient } from 'sanity'
import { ObjectInputProps } from 'sanity'

interface DesignItem {
  name: string
  type: string
}

interface FilterValue {
  constructionType?: string
  designs?: string[]
  diameters?: string[]
  finishes?: string[]
}

interface PromotionWheelFiltersProps extends ObjectInputProps {
  value?: FilterValue
}

const PromotionWheelFilters = forwardRef<HTMLDivElement, PromotionWheelFiltersProps>((props, ref) => {
  const { value, onChange, schemaType } = props
  const client = useClient()

  const constructionTypes = ['All', 'Forged', 'Flow Formed']

  const [init, setInit] = useState(true)
  const [allDesigns, setAllDesigns] = useState<DesignItem[]>([])
  const [allDiameters, setAllDiameters] = useState<string[]>([])
  const [allFinishes, setAllFinishes] = useState<string[]>([])
  const [designs, setDesigns] = useState<string[]>([])
  const [inputValues, setInputValues] = useState<FilterValue>(value || {})

  // Initialize data from Sanity
  useEffect(() => {
    client
      .fetch(
        `{
          "designs": *[_type == "design" && !(_id in path("drafts.**"))]|order(name asc){
            name,
            "type": line->construction
          },
          "diameters": *[_type == "diameter" && !(_id in path("drafts.**"))]|order(name asc).name,
          "finishes": *[_type == "finish" && !(_id in path("drafts.**"))]|order(name asc).name
        }`
      )
      .then(({ designs, diameters, finishes }) => {
        setAllDesigns(designs)
        setAllDiameters(diameters)
        setAllFinishes(finishes)
        setInit(false)
      })
      .catch((error) => {
        console.error('Error fetching promotion wheel filters data:', error)
        setInit(false)
      })
  }, [client])

  // Update designs based on construction type
  useEffect(() => {
    if (init) return
    
    const { constructionType = 'All' } = inputValues
    const newDesigns =
      constructionType === 'All'
        ? allDesigns.map(({ name }) => name)
        : allDesigns.filter((d) => d.type === constructionType).map(({ name }) => name)
    
    setDesigns(newDesigns)
  }, [init, inputValues.constructionType, allDesigns])

  const handleChange = useCallback(
    (newValue: FilterValue) => {
      setInputValues(newValue)
      onChange(newValue && Object.keys(newValue).length > 0 ? set(newValue) : unset())
    },
    [onChange]
  )

  const handleConstructionTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value: constructionType } = event.currentTarget
      const { constructionType: currentType = 'All' } = inputValues
      
      if (constructionType === currentType) return

      const newValue: FilterValue = {
        constructionType,
        designs: [],
        diameters: [],
        finishes: [],
      }
      
      handleChange(newValue)
    },
    [handleChange, inputValues]
  )

  const handleDesignsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked, value: design } = event.target
      const designs = [...(inputValues?.designs || [])]
      
      if (checked && !designs.includes(design)) {
        designs.push(design)
      }
      if (!checked) {
        const index = designs.indexOf(design)
        if (index > -1) designs.splice(index, 1)
      }

      const newValue = { ...inputValues, designs }
      handleChange(newValue)
    },
    [handleChange, inputValues]
  )

  const handleDiametersChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked, value: diameter } = event.target
      const diameters = [...(inputValues?.diameters || [])]
      
      if (checked && !diameters.includes(diameter)) {
        diameters.push(diameter)
      }
      if (!checked) {
        const index = diameters.indexOf(diameter)
        if (index > -1) diameters.splice(index, 1)
      }

      const newValue = { ...inputValues, diameters }
      handleChange(newValue)
    },
    [handleChange, inputValues]
  )

  const handleFinishesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked, value: finish } = event.target
      const finishes = [...(inputValues?.finishes || [])]
      
      if (checked && !finishes.includes(finish)) {
        finishes.push(finish)
      }
      if (!checked) {
        const index = finishes.indexOf(finish)
        if (index > -1) finishes.splice(index, 1)
      }

      const newValue = { ...inputValues, finishes }
      handleChange(newValue)
    },
    [handleChange, inputValues]
  )

  return (
    <Stack gap={3} ref={ref}>
      <Box>
        <Label size={1} weight="medium">
          {schemaType.title || 'Promotion Wheel Filters'}
        </Label>
        {schemaType.description && (
          <Text size={1} muted style={{ marginTop: 4 }}>
            {schemaType.description}
          </Text>
        )}
      </Box>

      <Stack gap={5}>
        <Box
          paddingY={3}
          style={{ borderLeft: '1px solid var(--card-border-color)' }}
        >
          <Stack gap={3}>
            {/* Construction Type Selection */}
            <Box paddingX={3}>
              <Label size={1} weight="medium" style={{ marginBottom: 8, display: 'block' }}>
                Construction Type
              </Label>
              <Inline gap={3}>
                {constructionTypes.map((type, i) => (
                  <Box key={type}>
                    <Radio
                      id={`constructionType-${i}`}
                      checked={type === (inputValues?.constructionType || 'All')}
                      name="constructionType"
                      onChange={handleConstructionTypeChange}
                      value={type}
                    />
                    <label
                      htmlFor={`constructionType-${i}`}
                      style={{ marginLeft: '5px' }}
                    >
                      {type}
                    </label>
                  </Box>
                ))}
              </Inline>
            </Box>

            {init ? (
              <Card padding={4}>
                <Flex justify="center">
                  <Spinner muted />
                </Flex>
              </Card>
            ) : (
              <Stack gap={4}>
                {/* Designs */}
                {designs.length > 0 && (
                  <Box padding={3}>
                    <Label size={1} weight="medium" style={{ marginBottom: 8, display: 'block' }}>
                      Designs
                    </Label>
                    <Inline gap={3}>
                      {designs.map((design, i) => (
                        <Flex align="center" key={`design-${i}`}>
                          <Checkbox
                            id={`design-${i}`}
                            value={design}
                            checked={inputValues?.designs?.includes(design) || false}
                            onChange={handleDesignsChange}
                            style={{ display: 'block' }}
                          />
                          <Box flex={1} paddingLeft={3}>
                            <Text>
                              <label htmlFor={`design-${i}`}>{design}</label>
                            </Text>
                          </Box>
                        </Flex>
                      ))}
                    </Inline>
                  </Box>
                )}

                {/* Diameters */}
                {allDiameters.length > 0 && (
                  <Box padding={3}>
                    <Label size={1} weight="medium" style={{ marginBottom: 8, display: 'block' }}>
                      Diameters
                    </Label>
                    <Inline gap={3}>
                      {allDiameters.map((diameter, i) => (
                        <Flex align="center" key={`diameter-${i}`}>
                          <Checkbox
                            id={`diameter-${i}`}
                            value={diameter}
                            checked={inputValues?.diameters?.includes(diameter) || false}
                            onChange={handleDiametersChange}
                            style={{ display: 'block' }}
                          />
                          <Box flex={1} paddingLeft={3}>
                            <Text>
                              <label htmlFor={`diameter-${i}`}>{diameter}</label>
                            </Text>
                          </Box>
                        </Flex>
                      ))}
                    </Inline>
                  </Box>
                )}

                {/* Finishes */}
                {allFinishes.length > 0 && (
                  <Box padding={3}>
                    <Label size={1} weight="medium" style={{ marginBottom: 8, display: 'block' }}>
                      Finishes
                    </Label>
                    <Inline gap={3}>
                      {allFinishes.map((finish, i) => (
                        <Flex align="center" key={`finish-${i}`}>
                          <Checkbox
                            id={`finish-${i}`}
                            value={finish}
                            checked={inputValues?.finishes?.includes(finish) || false}
                            onChange={handleFinishesChange}
                            style={{ display: 'block' }}
                          />
                          <Box flex={1} paddingLeft={3}>
                            <Text>
                              <label htmlFor={`finish-${i}`}>{finish}</label>
                            </Text>
                          </Box>
                        </Flex>
                      ))}
                    </Inline>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>
    </Stack>
  )
})

PromotionWheelFilters.displayName = 'PromotionWheelFilters'

export default PromotionWheelFilters